import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { miningAPI, walletAPI, getErrorMessage } from '../../utils/api';
import { formatCurrency } from '../../utils/helpers';
import { Button } from '../../components/ui/button';
import { Cpu, Check, Download, ArrowRight, Activity } from 'lucide-react';

const PACKAGES = [
  {
    key: 'starter',
    name: 'Starter Miner',
    value: 1000,
    hashPower: 1.8,
    dailyYield: 3.20,
    popular: false,
  },
  {
    key: 'growth',
    name: 'Growth Miner',
    value: 10000,
    hashPower: 22,
    dailyYield: 39,
    popular: true,
  },
  {
    key: 'pro',
    name: 'Pro Miner',
    value: 50000,
    hashPower: 128,
    dailyYield: 225,
    popular: false,
  },
  {
    key: 'enterprise',
    name: 'Enterprise Miner',
    value: 200000,
    hashPower: 620,
    dailyYield: 1080,
    popular: false,
  },
];

const gbp = (n) => formatCurrency(Number(n || 0), 'GBP');
const fmtHash = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(2)} PH/s`;
  return `${v.toFixed(v < 10 ? 2 : 0)} TH/s`;
};

const pad2 = (n) => String(Math.max(0, Math.floor(n))).padStart(2, '0');

const useCountdownTo = (targetIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!targetIso) return '00:00:00';
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, Math.floor((target - now) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
};

// ---------- Packages view --------------------------------------------------
const PackagesView = ({ stats, wallets, onPurchased }) => {
  const [selectedKey, setSelectedKey] = useState('growth');
  const [walletSymbol, setWalletSymbol] = useState('');
  const [amount, setAmount] = useState(10000);
  const [busy, setBusy] = useState(false);

  const selected = useMemo(
    () => PACKAGES.find((p) => p.key === selectedKey) || PACKAGES[0],
    [selectedKey]
  );

  useEffect(() => {
    setAmount(selected.value);
  }, [selected]);

  useEffect(() => {
    if (!walletSymbol && wallets.length) setWalletSymbol(wallets[0].asset_symbol);
  }, [wallets, walletSymbol]);

  const activeWallet = wallets.find((w) => w.asset_symbol === walletSymbol);
  const walletBalance = Number(
    activeWallet?.available_balance ?? activeWallet?.balance ?? 0
  );

  const submit = async () => {
    if (!walletSymbol) {
      toast.error('Pick a payment wallet');
      return;
    }
    if (walletBalance < selected.value) {
      toast.error(`Insufficient ${walletSymbol} balance for this contract`);
      return;
    }
    setBusy(true);
    try {
      const res = await miningAPI.purchase({
        package_name: selected.key,
        payment_wallet_symbol: walletSymbol,
        amount: selected.value,
      });
      toast.success(`${selected.name} activated`);
      onPurchased(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Purchase failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatTile
          label="Active Mining Balance"
          value={gbp(stats?.active_package_value || 0)}
          sub="BTC"
        />
        <StatTile
          label="Total Rewards"
          value={gbp(stats?.total_mined || 0)}
          sub="BTC"
          accent
        />
        <StatTile
          label="Hash Power"
          value={fmtHash(stats?.hash_power_ths || 0)}
          sub="Combined hashrate"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Package grid */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          {PACKAGES.map((p) => {
            const isSelected = p.key === selectedKey;
            return (
              <button
                key={p.key}
                onClick={() => setSelectedKey(p.key)}
                className={[
                  'relative text-left rounded-xl p-5 transition-all cursor-pointer',
                  isSelected
                    ? 'bg-white dark:bg-gray-800 border-2 border-blue-500 dark:border-blue-400 shadow-md'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-md',
                ].join(' ')}
                data-testid={`mining-pkg-${p.key}`}
              >
                {p.popular && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    POPULAR
                  </span>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-4 h-4 text-blue-600" />
                  <h3 className="text-gray-900 dark:text-white font-semibold text-base">{p.name}</h3>
                </div>
                <div className="text-gray-900 dark:text-white font-bold text-xl mb-3">
                  {gbp(p.value)}
                </div>
                <div className="space-y-1.5">
                  <Row label="Hash Power" value={fmtHash(p.hashPower)} />
                  <Row
                    label="Estimated Daily"
                    value={`${gbp(p.dailyYield)}/day`}
                    accent
                  />
                  <Row label="Activation" value="Instant" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Order summary panel */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm h-fit">
          <h3 className="text-gray-900 dark:text-white font-semibold text-base">Order summary</h3>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Configure your mining contract</p>

          <div className="rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3 mb-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-gray-900 dark:text-white font-semibold">{selected.name}</div>
                <div className="text-gray-500 dark:text-gray-400 text-xs">
                  {fmtHash(selected.hashPower)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-gray-900 dark:text-white font-bold">{gbp(selected.value)}</div>
                <div className="text-green-600 dark:text-green-400 text-xs">
                  {gbp(selected.dailyYield)}/day
                </div>
              </div>
            </div>
          </div>

          <Label>Payment wallet</Label>
          <select
            value={walletSymbol}
            onChange={(e) => setWalletSymbol(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 mb-4 text-sm focus:outline-none focus:border-blue-400"
            data-testid="mining-wallet-select"
          >
            {wallets.length === 0 && <option value="">No wallets available</option>}
            {wallets.map((w) => (
              <option key={w.asset_symbol} value={w.asset_symbol}>
                {w.asset_symbol} — {Number(w.available_balance ?? w.balance ?? 0).toFixed(2)}
              </option>
            ))}
          </select>

          <Label>Token amount</Label>
          <div className="flex gap-2 mb-4">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
            />
            <button
              type="button"
              onClick={() => setAmount(selected.value)}
              className="bg-gray-900 dark:bg-gray-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-500 transition-colors"
            >
              MAX
            </button>
          </div>

          <div className="rounded-lg bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 p-3 mb-4 space-y-2">
            <Row label="Mining Power" value={fmtHash(selected.hashPower)} />
            <Row
              label="Projected Yield"
              value={`${gbp(selected.dailyYield)}/day`}
              accent
            />
            <Row label="Activation" value="Instant" />
          </div>

          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-900 dark:text-white font-bold text-base">Total</span>
            <span className="text-gray-900 dark:text-white font-bold text-base">
              {gbp(selected.value)}
            </span>
          </div>

          <Button
            onClick={submit}
            disabled={busy || wallets.length === 0}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="mining-purchase"
          >
            {busy ? 'Processing…' : 'Proceed to Payment'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------- Confirmation view ----------------------------------------------
const ConfirmationView = ({ contract, onContinue }) => {
  const [progress, setProgress] = useState(0);
  const [countdown, setCountdown] = useState(15 * 60);

  useEffect(() => {
    const t = setTimeout(() => setProgress(85), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const i = setInterval(
      () => setCountdown((c) => (c > 0 ? c - 1 : 0)),
      1000
    );
    return () => clearInterval(i);
  }, []);

  const downloadReceipt = () => {
    const lines = [
      'Aqua Mining — Purchase Receipt',
      '=================================',
      `Order ID:           ${contract?.order_id || ''}`,
      `Package:            ${contract?.package_name || ''}`,
      `Package Value:      ${gbp(contract?.package_value)}`,
      `Hash Power:         ${fmtHash(contract?.hash_power_ths)}`,
      `Daily Yield:        ${gbp(contract?.daily_yield_estimate)}`,
      `Payment Wallet:     ${contract?.payment_wallet_symbol || ''}`,
      `Status:             ${contract?.status || ''}`,
      `Cycle Start:        ${contract?.cycle_start || ''}`,
      `Next Payout:        ${contract?.next_payout || ''}`,
      '',
      'Thank you for mining with Aqua.',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mining-receipt-${contract?.order_id || 'order'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const mm = Math.floor(countdown / 60);
  const ss = countdown % 60;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 shadow-sm">
        <div className="flex flex-col items-center text-center mb-6">
          <div
            className="w-16 h-16 rounded-full bg-green-100 border border-green-200 flex items-center justify-center mb-3"
            style={{ animation: 'mining-pop 0.5s ease-out' }}
          >
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-gray-900 dark:text-white text-2xl font-bold">
            Thanks for purchasing
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Your mining contract is being activated
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Chip label="Package value" value={gbp(contract?.package_value)} />
          <Chip label="Status" value="Confirmed" accent />
          <Chip
            label="Payment"
            value={`${contract?.payment_wallet_symbol || 'BTC'} Wallet`}
          />
          <Chip label="Mining Start" value="Immediately" />
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <RowKV k="Order ID" v={contract?.order_id || '—'} />
          <RowKV
            k="Mining Tokens"
            v={`${gbp(contract?.total_tokens)} MTK`}
          />
          <RowKV
            k="Estimated Daily Yield"
            v={gbp(contract?.daily_yield_estimate)}
            accent
          />
          <RowKV k="Network Fee" v="Included" last />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button
            onClick={onContinue}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="mining-view-progress"
          >
            View Mining Progress <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            variant="outline"
            onClick={downloadReceipt}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Download className="w-4 h-4 mr-1" /> Download Receipt
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm h-fit">
        <h3 className="text-gray-900 dark:text-white font-semibold text-base mb-1">Mining Ready</h3>
        <p className="text-gray-500 dark:text-gray-400 text-xs mb-4">
          Hash power is warming up — first rewards land shortly.
        </p>

        <div className="mb-2 text-xs text-gray-500 dark:text-gray-400 flex justify-between">
          <span>Activation progress</span>
          <span className="text-gray-700 dark:text-white font-medium">{progress}%</span>
        </div>
        <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-blue-500 rounded-full"
            style={{
              width: `${progress}%`,
              transition: 'width 3s ease-out',
            }}
          />
        </div>

        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Next update
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
            {pad2(0)}:{pad2(mm)}:{pad2(ss)}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes mining-pop {
          0% { transform: scale(0); opacity: 0; }
          70% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

// ---------- Dashboard view -------------------------------------------------
const DashboardView = ({ stats: initialStats }) => {
  const [stats, setStats] = useState(initialStats || null);
  const [chart, setChart] = useState([]);

  const loadStats = useCallback(async () => {
    try {
      const r = await miningAPI.getStats();
      setStats(r.data);
    } catch (err) {
      // silent
    }
  }, []);

  const loadChart = useCallback(async () => {
    try {
      const r = await miningAPI.getRewardsChart(24);
      setChart(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadChart();
    const s = setInterval(loadStats, 30_000);
    const c = setInterval(loadChart, 60_000);
    return () => {
      clearInterval(s);
      clearInterval(c);
    };
  }, [loadStats, loadChart]);

  const payoutCountdown = useCountdownTo(stats?.next_payout);
  const cyclePct = Math.max(
    0,
    Math.min(100, Number(stats?.cycle_percent_complete || 0))
  );
  const tickerRewardLabel = gbp(stats?.today_mined || 0);

  const chartData = useMemo(() => {
    if (!chart.length) return [];
    return chart.map((r) => ({
      t: new Date(r.recorded_at).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      amount: Number(r.amount || 0),
    }));
  }, [chart]);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-blue-600" />
          <div>
            <div className="text-gray-900 dark:text-white font-semibold">
              Mining contract active
            </div>
            <div className="text-gray-500 dark:text-gray-400 text-xs">
              {stats?.package_name ? stats.package_name.toUpperCase() : ''}
            </div>
          </div>
        </div>
        <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700 text-xs font-semibold px-3 py-1 rounded-full">
          Active
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Active Package"
          value={gbp(stats?.active_package_value || 0)}
        />
        <StatTile
          label="Hash Power"
          value={fmtHash(stats?.hash_power_ths || 0)}
        />
        <StatTile
          label="Today's Rewards"
          value={gbp(stats?.today_mined || 0)}
          sub="estimated"
          accent
        />
        <StatTile
          label="Total Mined"
          value={gbp(stats?.total_mined || 0)}
          sub="since purchase"
          accent
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="text-gray-900 dark:text-white font-semibold">Mining Output</h3>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Live reward activity</p>
            </div>
            <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">BTC</span>
          </div>
          <div className="h-72">
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                Rewards will appear within ~1 minute…
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="mineGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="t"
                    tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    minTickGap={32}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      color: '#111827',
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke="#1D4ED8"
                    strokeWidth={2}
                    fill="url(#mineGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm">
          <h3 className="text-gray-900 dark:text-white font-semibold mb-4">Mining Progress</h3>
          <Donut percent={cyclePct} />
          <div className="mt-5 space-y-2.5">
            <RowKVCompact
              k="Cycle Started"
              v={
                stats?.cycle_start
                  ? new Date(stats.cycle_start).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'
              }
            />
            <RowKVCompact k="Next Payout" v={payoutCountdown} accent />
            <RowKVCompact k="Reward Wallet" v="BTC Wallet" />
            <RowKVCompact
              k="Contract Status"
              v={
                stats?.contract_status === 'active'
                  ? 'Running'
                  : stats?.contract_status || '—'
              }
              accent={stats?.contract_status === 'active'}
            />
          </div>
        </div>
      </div>

      <Ticker rewardLabel={tickerRewardLabel} />
    </div>
  );
};

// ---------- Small building blocks ------------------------------------------
const StatTile = ({ label, value, sub, accent }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm">
    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
      {label}
    </div>
    <div
      className={[
        'text-2xl font-bold mt-1',
        accent ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white',
      ].join(' ')}
    >
      {value}
    </div>
    {sub && <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</div>}
  </div>
);

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span
      className={
        accent
          ? 'text-green-600 dark:text-green-400 font-semibold'
          : 'text-gray-900 dark:text-white font-medium'
      }
    >
      {value}
    </span>
  </div>
);

const RowKV = ({ k, v, accent, last }) => (
  <div
    className={[
      'flex justify-between items-center px-4 py-3 text-sm',
      last ? '' : 'border-b border-gray-100 dark:border-gray-700',
    ].join(' ')}
  >
    <span className="text-gray-500 dark:text-gray-400">{k}</span>
    <span
      className={
        accent
          ? 'text-green-600 dark:text-green-400 font-semibold text-right'
          : 'text-gray-900 dark:text-white font-medium text-right'
      }
    >
      {v}
    </span>
  </div>
);

const RowKVCompact = ({ k, v, accent }) => (
  <div className="flex justify-between items-center">
    <span className="text-gray-500 dark:text-gray-400 text-sm">{k}</span>
    <span
      className={[
        'text-sm tabular-nums',
        accent ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-900 dark:text-white font-medium',
      ].join(' ')}
    >
      {v}
    </span>
  </div>
);

const Chip = ({ label, value, accent }) => (
  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 text-center">
    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
    <div
      className={[
        'font-semibold text-lg mt-1',
        accent ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white',
      ].join(' ')}
    >
      {value}
    </div>
  </div>
);

const Label = ({ children }) => (
  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
    {children}
  </div>
);

const Donut = ({ percent }) => {
  const R = 60;
  const C = 2 * Math.PI * R;
  const offset = C - (percent / 100) * C;
  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg width="160" height="160" viewBox="0 0 160 160" className="-rotate-90">
        <circle
          cx="80"
          cy="80"
          r={R}
          stroke="#F3F4F6"
          strokeWidth="12"
          fill="none"
        />
        <circle
          cx="80"
          cy="80"
          r={R}
          stroke="#22C55E"
          strokeWidth="12"
          fill="none"
          strokeDasharray={C}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
          {Math.round(percent)}%
        </div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          cycle
        </div>
      </div>
    </div>
  );
};

const Ticker = ({ rewardLabel }) => {
  const [scanIn, setScanIn] = useState(45);
  useEffect(() => {
    const t = setInterval(
      () => setScanIn((v) => (v > 0 ? v - 1 : 60)),
      1000
    );
    return () => clearInterval(t);
  }, []);
  const items = [
    { text: 'Mining job accepted', accent: false },
    { text: `Reward calculated: ${rewardLabel}`, accent: true },
    {
      text: `Next block scan in ${pad2(Math.floor(scanIn / 60))}:${pad2(
        scanIn % 60
      )}`,
      accent: false,
    },
  ];
  return (
    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 flex items-center gap-4 overflow-hidden">
      <span className="text-gray-500 dark:text-gray-400 text-sm font-medium shrink-0">
        Latest activity
      </span>
      <div className="flex-1 flex items-center gap-6 flex-wrap">
        {items.map((it, idx) => (
          <React.Fragment key={idx}>
            <span
              className={[
                'flex items-center gap-2 text-sm whitespace-nowrap',
                it.accent
                  ? 'text-green-600 dark:text-green-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300',
              ].join(' ')}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              {it.text}
            </span>
            {idx < items.length - 1 && (
              <span className="text-gray-300 dark:text-gray-600 text-xs">•</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ---------- Page shell -----------------------------------------------------
const BitcoinMining = () => {
  const [view, setView] = useState('packages');
  const [contract, setContract] = useState(null);
  const [stats, setStats] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const [contractRes, statsRes, walletsRes] = await Promise.all([
        miningAPI.getContract(),
        miningAPI.getStats(),
        walletAPI.getAll().catch(() => ({ data: [] })),
      ]);
      if (!mounted.current) return;
      setContract(contractRes.data);
      setStats(statsRes.data);
      setWallets(Array.isArray(walletsRes.data) ? walletsRes.data : []);
      if (contractRes.data && contractRes.data.status === 'active') {
        setView('dashboard');
      } else {
        setView('packages');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load mining data'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn p-6" data-testid="bitcoin-mining-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Cpu className="w-6 h-6 text-blue-600" /> Bitcoin Mining
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
          Activate a hash-power contract and earn daily BTC rewards
        </p>
      </div>

      {view === 'packages' && (
        <PackagesView
          stats={stats}
          wallets={wallets}
          onPurchased={(c) => {
            setContract(c);
            setStats((s) => ({
              ...(s || {}),
              active_package_value: c.package_value,
              hash_power_ths: c.hash_power_ths,
              daily_yield_estimate: c.daily_yield_estimate,
              cycle_start: c.cycle_start,
              next_payout: c.next_payout,
              contract_status: c.status,
              order_id: c.order_id,
              package_name: c.package_name,
              today_mined: 0,
              total_mined: 0,
              cycle_percent_complete: 0,
            }));
            setView('confirmation');
          }}
        />
      )}

      {view === 'confirmation' && (
        <ConfirmationView
          contract={contract}
          onContinue={() => {
            bootstrap();
            setView('dashboard');
          }}
        />
      )}

      {view === 'dashboard' && <DashboardView stats={stats} />}
    </div>
  );
};

export default BitcoinMining;
